import React, { useState, useEffect, useCallback } from 'react';
import { getTabStyles, getTabHoverStyles, restoreTabStyles } from '../../utils/tabColors';
import permissionService from '../../services/permissionService';

const RolesPermissionsTab = ({ scopeType, scopeId, currentUserId, canAccessCustomRoles = true }) => {
  const [subTab, setSubTab] = useState('default-roles');
  const [roles, setRoles] = useState([]);
  const [defaultRoles, setDefaultRoles] = useState([]);
  const [publicRoles, setPublicRoles] = useState([]);
  const [permissionMatrix, setPermissionMatrix] = useState({ roles: [], permissions: [] });
  const [roleAssignments, setRoleAssignments] = useState([]);
  const [auditLog, setAuditLog] = useState({ items: [], totalCount: 0, page: 1, pageSize: 50 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redirect Teams users away from custom-roles if they try to access it directly
  useEffect(() => {
    if (subTab === 'custom-roles' && !canAccessCustomRoles) {
      setSubTab('default-roles');
    }
  }, [subTab, canAccessCustomRoles]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      switch (subTab) {
        case 'default-roles':
          const defaults = await permissionService.getDefaultRoles();
          setDefaultRoles(defaults);
          break;
        case 'custom-roles':
          const publicRolesList = await permissionService.getPublicRoles();
          const scoped = await permissionService.getRoles(scopeType, scopeId);
          setPublicRoles(publicRolesList);
          setRoles(scoped);
          break;
        case 'permission-matrix':
          const matrix = await permissionService.getPermissionMatrix();
          setPermissionMatrix(matrix);
          break;
        case 'role-assignment':
          const assignments = await permissionService.getScopeRoleAssignments(scopeType, scopeId);
          setRoleAssignments(assignments);
          break;
        case 'audit-log':
          const log = await permissionService.getAuditLog({ scopeType, scopeId, page: auditLog.page, pageSize: auditLog.pageSize });
          setAuditLog(log);
          break;
        default:
          // No action needed for unknown tabs
          break;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [scopeType, scopeId, subTab, auditLog.page, auditLog.pageSize]);

  useEffect(() => {
    if (scopeType && scopeId) {
      loadData();
    }
  }, [scopeType, scopeId, subTab, loadData]);

  const subTabs = [
    { id: 'default-roles', label: 'Default Roles' },
    ...(canAccessCustomRoles ? [{ id: 'custom-roles', label: 'Custom Roles' }] : []),
    { id: 'permission-matrix', label: 'Permission Matrix' },
    { id: 'role-assignment', label: 'Role Assignment' },
    { id: 'audit-log', label: 'Audit Log' }
  ];

  return (
    <div className="dashboard-card" style={{ padding: 16 }}>
      <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: 20, fontWeight: 600 }}>
        Roles & Permissions
      </h2>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
        {subTabs.map(t => (
          <button
            key={t.id}
            className="tab-button"
            style={getTabStyles(2, subTab === t.id)}
            onMouseEnter={(e) => {
              if (subTab !== t.id) {
                Object.assign(e.currentTarget.style, getTabHoverStyles(2));
              }
            }}
            onMouseLeave={(e) => {
              if (subTab !== t.id) {
                restoreTabStyles(e.currentTarget, 2, false);
              }
            }}
            onClick={() => setSubTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>
          Error: {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      ) : (
        <>
          {subTab === 'default-roles' && (
            <DefaultRolesView roles={defaultRoles} scopeType={scopeType} />
          )}
          {subTab === 'custom-roles' && !canAccessCustomRoles && (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <h3 style={{ marginTop: 0, marginBottom: 12, color: '#dc2626' }}>Feature Not Available</h3>
              <p style={{ marginBottom: 20, color: '#6b7280' }}>
                This feature is only available in Enterprise tier.
              </p>
              <a 
                href="/pricing" 
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  backgroundColor: '#4c51bf',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontWeight: 600
                }}
              >
                Upgrade to Enterprise
              </a>
            </div>
          )}

          {subTab === 'custom-roles' && canAccessCustomRoles && (
            <CustomRolesView roles={roles} publicRoles={publicRoles} scopeType={scopeType} scopeId={scopeId} onRefresh={loadData} />
          )}
          {subTab === 'permission-matrix' && (
            <PermissionMatrixView matrix={permissionMatrix} />
          )}
          {subTab === 'role-assignment' && (
            <RoleAssignmentView 
              assignments={roleAssignments} 
              scopeType={scopeType} 
              scopeId={scopeId}
              onRefresh={loadData}
            />
          )}
          {subTab === 'audit-log' && (
            <AuditLogView log={auditLog} scopeType={scopeType} scopeId={scopeId} />
          )}
        </>
      )}
    </div>
  );
};

// Default Roles View Component
const DefaultRolesView = ({ roles, scopeType }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRole(null);
  };

  return (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Default System Roles</h3>
      <div style={{ display: 'grid', gap: 12 }}>
        {roles.map(role => (
          <div 
            key={role.id} 
            onClick={() => handleRoleClick(role)}
            style={{ 
              border: '1px solid #e5e7eb', 
              borderRadius: 8, 
              padding: 16, 
              background: '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#4f46e5';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(79, 70, 229, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{role.name}</h4>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 14 }}>{role.description}</p>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginLeft: 16 }}>
                {role.requiresPaidUser ? 'Requires Team/Enterprise User' : 'Available'}
              </div>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#4f46e5', fontWeight: 500 }}>
              Click to view permissions →
            </div>
          </div>
        ))}
      </div>

      {/* Drawer for showing permissions */}
      {drawerOpen && selectedRole && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            right: 0, 
            bottom: 0, 
            width: '400px', 
            background: '#fff', 
            boxShadow: '-4px 0 12px rgba(0,0,0,0.15)', 
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{selectedRole.name}</h3>
            <button
              onClick={closeDrawer}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 24,
                cursor: 'pointer',
                color: '#6b7280',
                padding: '0 8px',
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>
          <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: 14 }}>{selectedRole.description}</p>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
                {selectedRole.requiresPaidUser 
                  ? (scopeType === 'Team' ? 'Requires TeamUser Identity role' : scopeType === 'Organization' ? 'Requires EnterpriseUser Identity role' : 'Requires PaidUser subscription')
                  : 'Available'}
              </div>
            </div>
            {selectedRole.permissions && selectedRole.permissions.length > 0 ? (
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 600 }}>Permissions ({selectedRole.permissions.length})</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedRole.permissions.map((perm, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        padding: '10px 12px', 
                        background: '#f9fafb', 
                        borderRadius: 6, 
                        fontSize: 13,
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      {perm}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>
                No permissions assigned to this role
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {drawerOpen && (
        <div
          onClick={closeDrawer}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
        />
      )}
    </div>
  );
};

// Custom Roles View Component
const CustomRolesView = ({ roles, publicRoles, scopeType, scopeId, onRefresh }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Custom Roles</h3>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '8px 16px',
            background: '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: 14
          }}
        >
          Create Custom Role
        </button>
      </div>

      <div style={{ marginBottom: 24 }}>
        <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Public/Shared Roles</h4>
        <div style={{ display: 'grid', gap: 12 }}>
          {publicRoles.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No public roles available</div>
          ) : (
            publicRoles.map(role => (
              <div key={role.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <h5 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{role.name}</h5>
                    <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 13 }}>{role.description}</p>
                  </div>
                  <span style={{ fontSize: 12, color: '#059669', padding: '2px 8px', background: '#d1fae5', borderRadius: 4 }}>
                    Public
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div>
        <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Scoped Roles</h4>
        <div style={{ display: 'grid', gap: 12 }}>
          {roles.filter(r => !r.isSystemRole && r.scopeId === scopeId).length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No scoped roles for this {scopeType}</div>
          ) : (
            roles.filter(r => !r.isSystemRole && r.scopeId === scopeId).map(role => (
              <div key={role.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <h5 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{role.name}</h5>
                    <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 13 }}>{role.description}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateCustomRoleModal
          onClose={() => setShowCreateModal(false)}
          scopeType={scopeType}
          scopeId={scopeId}
          onSuccess={() => {
            setShowCreateModal(false);
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </div>
  );
};

// Permission Matrix View Component
const PermissionMatrixView = ({ matrix }) => {
  return (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Permission Matrix</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: '#f9fafb' }}>
                Permission
              </th>
              {matrix.roles?.map(role => (
                <th key={role.id} style={{ padding: '12px', textAlign: 'center', fontWeight: 600, minWidth: 120 }}>
                  {role.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.permissions?.map((category, catIdx) => (
              <React.Fragment key={catIdx}>
                <tr>
                  <td colSpan={(matrix.roles?.length || 0) + 1} style={{ padding: '8px 12px', background: '#f3f4f6', fontWeight: 600, fontSize: 12 }}>
                    {category.category}
                  </td>
                </tr>
                {category.permissions?.map(perm => (
                  <tr key={perm.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '10px 12px', position: 'sticky', left: 0, background: '#fff' }}>
                      {perm.name}
                    </td>
                    {matrix.roles?.map(role => {
                      const hasPermission = perm.roles?.find(r => r.roleId === role.id)?.hasPermission || false;
                      return (
                        <td key={role.id} style={{ padding: '10px', textAlign: 'center' }}>
                          {hasPermission ? '✓' : '✗'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Role Assignment View Component
const RoleAssignmentView = ({ assignments, scopeType, scopeId, onRefresh }) => {
  const [showAssignModal, setShowAssignModal] = useState(false);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Role Assignments</h3>
        <button
          onClick={() => setShowAssignModal(true)}
          style={{
            padding: '8px 16px',
            background: '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: 14
          }}
        >
          Assign Role
        </button>
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {assignments.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No role assignments</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>User</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Assigned At</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map(assignment => (
                <tr key={assignment.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px' }}>{assignment.userEmail} ({assignment.userName})</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ padding: '4px 8px', background: '#dbeafe', borderRadius: 4, fontSize: 12 }}>
                      {assignment.roleName}
                    </span>
                  </td>
                  <td style={{ padding: '12px', color: '#6b7280' }}>
                    {new Date(assignment.assignedAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <button
                      onClick={async () => {
                        if (window.confirm('Remove this role assignment?')) {
                          try {
                            await permissionService.removeRoleAssignment(assignment.id);
                            onRefresh();
                          } catch (err) {
                            window.alert('Error removing assignment: ' + err.message);
                          }
                        }
                      }}
                      style={{
                        padding: '4px 8px',
                        background: '#fee2e2',
                        color: '#991b1b',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAssignModal && (
        <AssignRoleModal
          onClose={() => setShowAssignModal(false)}
          scopeType={scopeType}
          scopeId={scopeId}
          onSuccess={() => {
            setShowAssignModal(false);
            onRefresh();
          }}
        />
      )}
    </div>
  );
};

// Audit Log View Component
const AuditLogView = ({ log, scopeType, scopeId }) => {
  return (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Permission Audit Log</h3>
      <div style={{ marginBottom: 16, fontSize: 14, color: '#6b7280' }}>
        Showing {log.items?.length || 0} of {log.totalCount} entries
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {log.items?.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No audit log entries</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Action</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>User</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Target</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {log.items.map(entry => (
                <tr key={entry.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px', color: '#6b7280' }}>
                    {new Date(entry.createdAt).toLocaleString()}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ padding: '4px 8px', background: '#f3f4f6', borderRadius: 4, fontSize: 12 }}>
                      {entry.action}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>{entry.userEmail}</td>
                  <td style={{ padding: '12px', color: '#6b7280' }}>
                    {entry.targetUserEmail || '-'}
                  </td>
                  <td style={{ padding: '12px', color: '#6b7280', fontSize: 12 }}>
                    {entry.details ? JSON.parse(entry.details)?.RoleName || '-' : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Create Custom Role Modal Component
const CreateCustomRoleModal = ({ onClose, scopeType, scopeId, onSuccess }) => {
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [requiresPaidUser, setRequiresPaidUser] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissionsByCategory, setPermissionsByCategory] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadPermissions = async () => {
      setLoadingPermissions(true);
      setError(null);
      try {
        const permissions = await permissionService.getPermissionsByCategory();
        console.log('Permissions received:', permissions);
        if (!permissions || permissions.length === 0) {
          setError('No permissions found. Please ensure permissions are seeded in the database.');
        } else {
          // Normalize property names to handle both PascalCase and camelCase
          const normalized = permissions.map(cat => ({
            category: cat.category || cat.Category || 'Uncategorized',
            permissions: (cat.permissions || cat.Permissions || []).map(p => ({
              id: p.id || p.Id,
              name: p.name || p.Name,
              description: p.description || p.Description
            }))
          }));
          setPermissionsByCategory(normalized);
        }
      } catch (err) {
        console.error('Error loading permissions:', err);
        setError('Failed to load permissions: ' + (err.message || 'Unknown error') + '. Please check the browser console for details.');
      } finally {
        setLoadingPermissions(false);
      }
    };
    loadPermissions();
  }, []);

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSelectAllInCategory = (categoryPermissions) => {
    const categoryPermissionIds = categoryPermissions.map(p => p.id);
    const allSelected = categoryPermissionIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      // Deselect all in category
      setSelectedPermissions(prev => prev.filter(id => !categoryPermissionIds.includes(id)));
    } else {
      // Select all in category
      setSelectedPermissions(prev => {
        const newSelection = [...prev];
        categoryPermissionIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!roleName.trim()) {
      setError('Role name is required');
      return;
    }

    setSubmitting(true);
    setError(null);
    
    try {
      console.log('Creating role with data:', {
        name: roleName.trim(),
        description: description.trim() || null,
        scopeType: isPublic ? null : scopeType,
        scopeId: isPublic ? null : scopeId,
        requiresPaidUser: requiresPaidUser,
        permissionIds: selectedPermissions
      });
      
      const result = await permissionService.createRole({
        name: roleName.trim(),
        description: description.trim() || null,
        scopeType: isPublic ? null : scopeType,
        scopeId: isPublic ? null : scopeId,
        requiresPaidUser: requiresPaidUser,
        permissionIds: selectedPermissions
      });
      
      console.log('Role created successfully:', result);
      onSuccess();
    } catch (err) {
      console.error('Error creating role:', err);
      setError(err.message || 'Failed to create role');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 800, width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0, marginBottom: 20 }}>Create Custom Role</h3>
        
        {error && (
          <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Role Name */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                Role Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g., Content Manager"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: 6,
                  fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this role is for..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: 6,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Make Public/Shared */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#4f46e5'
                  }}
                />
                <span style={{ fontSize: 14, color: '#374151' }}>
                  Make this role public/shared (available to all teams/organizations)
                </span>
              </label>
              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280', paddingLeft: 26 }}>
                {isPublic 
                  ? 'This role will be available to all teams and organizations' 
                  : `This role will be scoped to this ${scopeType.toLowerCase()} only`}
              </p>
            </div>

            {/* Requires Paid User */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={requiresPaidUser}
                  onChange={(e) => setRequiresPaidUser(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#4f46e5'
                  }}
                />
                <span style={{ fontSize: 14, color: '#374151' }}>
                  Requires {scopeType === 'Team' ? 'TeamUser' : scopeType === 'Organization' ? 'EnterpriseUser' : 'PaidUser'} status
                </span>
              </label>
              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280', paddingLeft: 26 }}>
                Users must have {scopeType === 'Team' ? 'TeamUser' : scopeType === 'Organization' ? 'EnterpriseUser' : 'PaidUser'} identity role to be assigned this role
              </p>
            </div>

            {/* Permissions Selection */}
            <div>
              <label style={{ display: 'block', marginBottom: 12, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                Permissions
              </label>
              {loadingPermissions ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading permissions...</div>
              ) : permissionsByCategory.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No permissions available</div>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: '400px', overflowY: 'auto' }}>
                  {permissionsByCategory.map((category, catIdx) => {
                    const categoryPermissionIds = category.permissions.map(p => p.id);
                    const allSelected = categoryPermissionIds.length > 0 && categoryPermissionIds.every(id => selectedPermissions.includes(id));
                    
                    return (
                      <div key={catIdx} style={{ borderBottom: catIdx < permissionsByCategory.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                        <div style={{ padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>
                            {category.category || 'Uncategorized'}
                          </h4>
                          <button
                            type="button"
                            onClick={() => handleSelectAllInCategory(category.permissions)}
                            style={{
                              padding: '4px 12px',
                              background: allSelected ? '#fee2e2' : '#dbeafe',
                              color: allSelected ? '#991b1b' : '#1e40af',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 500
                            }}
                          >
                            {allSelected ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>
                        <div style={{ padding: '8px 16px' }}>
                          {category.permissions.map(permission => (
                            <label
                              key={permission.id}
                              style={{
                                display: 'flex',
                                alignItems: 'start',
                                gap: 8,
                                padding: '8px 0',
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(permission.id)}
                                onChange={() => handlePermissionToggle(permission.id)}
                                style={{
                                  marginTop: 2,
                                  width: '16px',
                                  height: '16px',
                                  cursor: 'pointer',
                                  accentColor: '#4f46e5',
                                  flexShrink: 0
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                                  {permission.name}
                                </div>
                                {permission.description && (
                                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                    {permission.description}
                                  </div>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {selectedPermissions.length > 0 && (
                <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#059669' }}>
                  {selectedPermissions.length} permission{selectedPermissions.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>

            {/* Scope Info */}
            <div style={{ padding: 12, background: '#eff6ff', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
              <strong>Scope:</strong> {isPublic ? 'Public/Shared (available to all)' : `${scopeType} (ID: ${scopeId})`}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
            <button 
              type="button"
              onClick={onClose} 
              disabled={submitting}
              style={{ 
                padding: '10px 20px', 
                border: '1px solid #e5e7eb', 
                borderRadius: 6, 
                cursor: submitting ? 'not-allowed' : 'pointer',
                background: '#fff',
                color: '#374151',
                fontSize: 14,
                fontWeight: 500,
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={submitting || loadingPermissions}
              style={{ 
                padding: '10px 20px', 
                background: submitting ? '#9ca3af' : '#4f46e5', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6, 
                cursor: submitting || loadingPermissions ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              {submitting ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AssignRoleModal = ({ onClose, scopeType, scopeId, onSuccess }) => {
  const [userId, setUserId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadRoles = async () => {
      setLoading(true);
      try {
        const roles = await permissionService.getRoles(scopeType, scopeId);
        const defaults = await permissionService.getDefaultRoles();
        const publicRoles = await permissionService.getPublicRoles();
        // Combine default, scoped, and public roles, filter out system roles that shouldn't be assignable
        // Remove duplicates by ID
        const roleMap = new Map();
        [...defaults, ...roles, ...publicRoles].forEach(r => {
          if (r.name !== 'Admin' && !roleMap.has(r.id)) { // Admin should not be assignable through UI
            roleMap.set(r.id, r);
          }
        });
        const allRoles = Array.from(roleMap.values());
        setAvailableRoles(allRoles);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadRoles();
  }, [scopeType, scopeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId.trim() || !selectedRoleId) {
      setError('Please provide both user ID and role');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await permissionService.assignRole(userId.trim(), parseInt(selectedRoleId), scopeType, scopeId);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to assign role');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 600, width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0, marginBottom: 20 }}>Assign Role</h3>
        
        {error && (
          <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                User ID <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter user ID"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: 6,
                  fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280' }}>
                Enter the user ID of the person you want to assign a role to
              </p>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                Role <span style={{ color: '#ef4444' }}>*</span>
              </label>
              {loading ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading roles...</div>
              ) : (
                <select
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: 6,
                    fontSize: 14,
                    background: '#fff',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select a role...</option>
                  {availableRoles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name} {role.isSystemRole ? '(System)' : ''} {role.requiresPaidUser ? (scopeType === 'Team' ? '- Requires TeamUser' : scopeType === 'Organization' ? '- Requires EnterpriseUser' : '- Requires PaidUser') : ''}
                    </option>
                  ))}
                </select>
              )}
              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280' }}>
                Select the role to assign. Note: This will replace any existing role assignment for this user in this {scopeType.toLowerCase()}.
              </p>
            </div>

            <div style={{ padding: 12, background: '#eff6ff', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
              <strong>Scope:</strong> {scopeType} (ID: {scopeId})
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
            <button 
              type="button"
              onClick={onClose} 
              disabled={submitting}
              style={{ 
                padding: '10px 20px', 
                border: '1px solid #e5e7eb', 
                borderRadius: 6, 
                cursor: submitting ? 'not-allowed' : 'pointer',
                background: '#fff',
                color: '#374151',
                fontSize: 14,
                fontWeight: 500,
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={submitting || loading}
              style={{ 
                padding: '10px 20px', 
                background: submitting ? '#9ca3af' : '#4f46e5', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6, 
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              {submitting ? 'Assigning...' : 'Assign Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RolesPermissionsTab;

